import { useQuery } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'
import { api } from './client'
import { ApiError, asProblem } from './problem'

/**
 * One wrapper around `openapi-fetch` so every page handles failure the same
 * way: the problem document becomes an `ApiError`, which `DataState` renders.
 *
 * `staleTime: Infinity` matches the `Cache-Control` the backend already sends --
 * the dataset changes about once a week -- and `placeholderData` keeps the
 * previous answer on screen while a new scenario loads, so adjusting a control
 * mutates the view instead of blanking it.
 */
export function useApi<T>(
  key: readonly unknown[],
  fetcher: () => Promise<{ data?: T; error?: unknown; response: Response }>,
  options: { enabled?: boolean } = {},
): UseQueryResult<T, ApiError> {
  return useQuery<T, ApiError>({
    queryKey: key,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: false,
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const { data, error, response } = await fetcher()
      if (error !== undefined || data === undefined) {
        throw new ApiError(asProblem(error, response.status))
      }
      return data
    },
  })
}

export { api }
